import { useEffect, useState } from "react";
import { api, formatError, formatNZD } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const thisMonth = () => new Date().toISOString().slice(0, 7);

function Row({ r, admin, onPaid }) {
  const markPaid = async () => {
    const ref = window.prompt(`Mark ${formatNZD(r.operator_share)} as paid to the ${r.region} operator for ${r.month}?\n\nBank transfer reference (optional):`, "");
    if (ref === null) return;
    try { await api.post("/admin/payouts/mark-paid", { region: r.region, month: r.month, reference: ref }); toast.success(`${r.region} marked paid`); onPaid(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`payout-row-${r.region}`}>
      <td className="py-2 pr-3 font-semibold">{r.region}<div className="text-[11px] text-slate-500 font-normal">{r.operator?.operator_name || "No operator"}</div></td>
      <td className="py-2 pr-3 text-right">{formatNZD(r.gross)}</td>
      <td className="py-2 pr-3 text-right text-slate-500">−{formatNZD(r.stripe_fees_est)}</td>
      <td className="py-2 pr-3 text-right">{formatNZD(r.net)}</td>
      <td className="py-2 pr-3 text-right font-bold text-emerald-800" data-testid={`payout-op-${r.region}`}>{formatNZD(r.operator_share)}</td>
      <td className="py-2 pr-3 text-right">{formatNZD(r.platform_share)}</td>
      <td className="py-2 text-right">
        {r.paid && r.outstanding <= 0 ? <Badge className="bg-emerald-700 hover:bg-emerald-700" title={r.paid.reference}>Paid {formatNZD(r.paid.amount)}</Badge>
          : admin && r.outstanding > 0 ? <div className="flex items-center justify-end gap-1">{r.paid && <span className="text-[10px] text-amber-700" data-testid={`payout-outstanding-${r.region}`}>{formatNZD(r.outstanding)} more owed</span>}<Button size="sm" variant="outline" onClick={markPaid} data-testid={`mark-paid-${r.region}`}>Mark paid</Button></div>
          : <span className="text-xs text-slate-400">{r.outstanding > 0 ? "Unpaid" : "—"}</span>}
      </td>
    </tr>
  );
}

export default function PayoutsCard({ region, admin }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const load = async () => {
    try { const res = await api.get("/staff/payouts", { params: { month, region } }); setData(res.data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [month, region]);
  const rows = data?.regions || [];
  const total = rows.reduce((s, r) => s + r.operator_share, 0);
  return (
    <Card data-testid="payouts-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="font-display text-xl">Monthly payouts</CardTitle>
          <CardDescription>Operator gets {data?.operator_share_pct ?? 65}% of what&apos;s left after Stripe fees. Counts Fiver Deal fees, $2 adverts, wallet top-ups and boosts. {data?.fee_note}.</CardDescription>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className="w-44" data-testid="payout-month" />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500 text-right">
            <th className="text-left pb-2">Region</th><th className="pb-2 pr-3">Taken</th><th className="pb-2 pr-3">Stripe fees</th><th className="pb-2 pr-3">After fees</th><th className="pb-2 pr-3">Operator</th><th className="pb-2 pr-3">Platform</th><th className="pb-2">Status</th>
          </tr></thead>
          <tbody>{rows.map((r) => <Row key={r.region} r={r} admin={admin} onPaid={load} />)}</tbody>
        </table>
        {admin && rows.length > 1 && <div className="text-sm font-semibold mt-3 text-right" data-testid="payout-total">Total owed to operators: {formatNZD(total)}</div>}
      </CardContent>
    </Card>
  );
}
